import React from 'react';
import { NavigationProp, useNavigation } from '@react-navigation/native';
import { useForm } from 'react-hook-form';
import FilterModal from '../../components/ModernFilters/FilterModal';
import PeriodSelector from '../../components/ModernFilters/PeriodSelector';
import RegionsList from '../../components/ModernFilters/RegionsList';
import ActingBodyList from '../../components/ModernFilters/dispatch/ActingBodyList';
import { EventsParamList } from '../../typings/Params';
import { OccurrencePriorities, TriggerType } from '../../schemas/dispatch';
import { translate } from '../../services/translate';
import PriorityList from '../../components/ModernFilters/dispatch/PriorityList';
import TriggerTypeList from '../../components/ModernFilters/dispatch/TriggerTypeList';
import OccurrenceTypeList from '../../components/ModernFilters/dispatch/OccurrenceTypeList';
import EventTagList from '../../components/ModernFilters/dispatch/EventTagList';
import RadioYesNo from '../../components/ModernFilters/RadioYesNo';

type FiltersForm = {
    actingBodyId: number | null;
    priority: OccurrencePriorities | null;
    triggerType: (TriggerType | 'manual')[];
    occurrenceTypeId: number | null;
    tags: number[];
    period: { from: Date | null; to: Date | null; };
    regions: { type: string; value: number[]; };
    dispatchStatus: 'yes' | 'no' | null;
};

interface Props {
    filters: EventsParamList['EventsBoard'];
    setFilters: React.Dispatch<React.SetStateAction<EventsParamList['EventsBoard']>>;
    authenticatedUser?: AuthenticatedUser | null;
}

function EventsFilter({ filters, setFilters, authenticatedUser }: Props) {
    const navigation = useNavigation<NavigationProp<EventsParamList, 'EventsBoard'>>();
    const form = useForm<FiltersForm>({
        mode: 'onChange',
        defaultValues: {
            actingBodyId: filters.actingBodyId ?? null,
            priority: filters.priority ?? null,
            triggerType: filters.triggerType ?? [],
            occurrenceTypeId: filters.occurrenceTypeId ?? null,
            tags: filters.tags ?? [],
            period: {
                from: filters.beginIn ? new Date(filters.beginIn) : null,
                to: filters.endIn ? new Date(filters.endIn) : null,
            },
            regions: {
                type: filters.polygonType ?? '',
                value: filters.mapPolygonIds ?? [],
            },
            dispatchStatus: filters.withDispatch ? 'yes' : filters.withoutDispatch ? 'no' : null,
        },
    });

    const handleFilter = (values: FiltersForm) => {
        const params = {
            priority: values.priority ?? undefined,
            triggerType: values.triggerType.length ? values.triggerType : undefined,
            occurrenceTypeId: values.occurrenceTypeId ?? undefined,
            tags: values.tags.length ? values.tags : undefined,
            actingBodyId: values.actingBodyId ?? undefined,
            beginIn: values.period.from?.getTime(),
            endIn: values.period.to?.getTime(),
            withDispatch: values.dispatchStatus === 'yes' ? true : undefined,
            withoutDispatch: values.dispatchStatus === 'no' ? true : undefined,
            mapPolygonIds: values.regions.value.length ? values.regions.value : undefined,
            polygonType: values.regions.type || undefined,
        };

        setFilters({ ...filters, ...params });
        navigation.setParams(params);
    };

    return <FilterModal
        form={form}
        onFilters={handleFilter}
        cleanValues={{
            actingBodyId: null,
            priority: null,
            triggerType: [],
            occurrenceTypeId: null,
            tags: [],
            period: { from: null, to: null },
            regions: { type: '', value: [] },
            dispatchStatus: null,
        }}
        filters={[{
            label: translate('organizational'),
            filters: [{
                componentHandlesCount: true,
                name: 'actingBodyId',
                label: translate('actingBody'),
                component: ActingBodyList,
                omit: !(authenticatedUser?.isAdmin || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_occurrences),
                cleans: ['triggerType', 'tags'],
            }],
        }, {
            label: translate('events'),
            filters: [{
                componentHandlesCount: true,
                name: 'priority',
                label: translate('priority'),
                component: PriorityList,
            }, {
                componentHandlesCount: true,
                name: 'triggerType',
                label: translate('integrationType'),
                component: TriggerTypeList,
            }, {
                componentHandlesCount: false,
                name: 'occurrenceTypeId',
                label: translate('occurrenceType'),
                getCount: OccurrenceTypeList.getCount,
                component: ({ name }) => <OccurrenceTypeList
                    name={name}
                    actingBodyId={form.watch('actingBodyId') ?? undefined}
                />,
            }, {
                componentHandlesCount: false,
                name: 'tags',
                label: translate('tags'),
                getCount: EventTagList.getCount,
                component: ({ name }) => <EventTagList
                    name={name}
                    actingBodyId={form.watch('actingBodyId') ?? undefined}
                />,
            }, {
                componentHandlesCount: true,
                name: 'period',
                label: translate('period'),
                component: PeriodSelector,
            }, {
                componentHandlesCount: true,
                name: 'regions',
                label: translate('regions'),
                component: RegionsList,
            }, {
                componentHandlesCount: true,
                name: 'dispatchStatus',
                label: translate('withDispatch'),
                component: RadioYesNo,
            }],
        }]}
        applyDisabled={!!form.watch('regions.type') && !form.watch('regions.value').length}
    />;
}

export default EventsFilter;
